/**
 * How much of the storage allowance is left.
 *
 * The gigabyte figures were sold for as long as the tiers existed and counted
 * nowhere (REQ-013, AC-006). Now that they are enforced, a traveller has to be
 * able to see them before an upload is refused — a limit you only meet by
 * hitting it is a worse experience than no limit at all.
 */

import { getStorageUsage, StorageUsage as Usage } from "@/services/api/storage";
import { HardDrive } from "lucide-react";
import React, { useEffect, useState } from "react";

export const StorageUsage: React.FC = () => {
  const [usage, setUsage] = useState<Usage | null>(null);

  useEffect(() => {
    getStorageUsage()
      .then(setUsage)
      .catch(() => setUsage(null));
  }, []);

  if (!usage || usage.limit === 0) return null;

  const percent = Math.min(100, Math.round((usage.used / usage.limit) * 100));
  const tight = percent >= 90;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="mb-2 flex items-center justify-between text-sm">
        <span className="inline-flex items-center gap-2 font-medium text-gray-700">
          <HardDrive className="h-4 w-4 text-gray-500" />
          Storage
        </span>
        <span
          className={tight ? "font-medium text-amber-700" : "text-gray-600"}
        >
          {usage.usedLabel} of {usage.limitLabel}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
        <div
          className={`h-full rounded-full ${tight ? "bg-amber-500" : "bg-primary-600"}`}
          style={{ width: `${percent}%` }}
        />
      </div>
      {tight && (
        <p className="mt-2 text-xs text-amber-700">
          Nearly full. Delete a document, or move up a plan for more room.
        </p>
      )}
    </div>
  );
};
